import { useMemo, useState } from "react";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { GridActionsCellItem, type GridColDef } from "@mui/x-data-grid";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import DeleteIcon from "@mui/icons-material/Delete";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import HistoryIcon from "@mui/icons-material/History";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import { api } from "../lib/api";
import { useAsync } from "../lib/hooks";
import { useI18n, type MessageKey } from "../lib/i18n";
import { COUNTRIES, COUNTRY_META, getCountry, type Country } from "../lib/country";
import { ONLINE_THRESHOLD_MINUTES, deviceState, type DeviceState } from "../lib/device";
import { newestFirst } from "../lib/format";
import type { Device } from "../lib/types";
import { Button, Card, ErrorBox, Loading } from "../components/ui";
import { DataTable } from "../components/DataTable";
import { ApiKeyModal, DeviceForm, DeviceResultsModal } from "../components/device-dialogs";

const STATE_COLOR: Record<DeviceState, "success" | "default" | "error"> = {
  online: "success",
  offline: "default",
  disabled: "error",
};

export function Devices({ navigate }: { navigate: (to: string) => void }) {
  const { t, fmtDate, fmtAgo } = useI18n();
  const { data, loading, error, reload } = useAsync(() => api.devices.list(), []);
  const [editing, setEditing] = useState<Device | "new" | null>(null);
  const [revealed, setRevealed] = useState<Device | null>(null);
  const [viewing, setViewing] = useState<Device | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const devices = data ?? [];

  async function run(key: string, fn: () => Promise<unknown>) {
    setBusy(key);
    setActionError(null);
    try {
      await fn();
      await reload();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  async function rotate(d: Device) {
    if (!confirm(t("devices.confirmRotate", { name: d.name }))) return;
    setBusy(`rotate-${d.id}`);
    setActionError(null);
    try {
      setRevealed(await api.devices.rotateKey(d.id));
      await reload();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  async function remove(d: Device) {
    if (!confirm(t("devices.confirmDelete", { name: d.name }))) return;
    await run(`del-${d.id}`, () => api.devices.remove(d.id));
  }

  const columns = useMemo<GridColDef<Device>[]>(
    () => [
      {
        field: "name",
        headerName: t("devices.nameLabel"),
        flex: 1.2,
        minWidth: 150,
        renderCell: ({ row }) => (
          <Stack sx={{ lineHeight: 1.3, py: 0.5 }}>
            <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
              {row.name}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {[row.platform, row.model, row.appVersion].filter(Boolean).join(" · ") || "—"}
            </Typography>
          </Stack>
        ),
      },
      {
        field: "state",
        headerName: t("table.status"),
        flex: 0.6,
        minWidth: 110,
        // Trạng thái suy ra từ lastSeenAt nên phải tự tính để sort/filter dùng được.
        valueGetter: (_v, row) => deviceState(row),
        renderCell: ({ row }) => {
          const state = deviceState(row);
          return (
            <Tooltip
              title={
                state === "online" ? t("devices.onlineHint", { n: ONLINE_THRESHOLD_MINUTES }) : ""
              }
            >
              <Chip size="small" color={STATE_COLOR[state]} label={t(`devices.state.${state}`)} />
            </Tooltip>
          );
        },
      },
      {
        field: "country",
        headerName: t("table.country"),
        flex: 0.6,
        minWidth: 110,
        renderCell: ({ row }) => (
          <span>
            {COUNTRY_META[row.country]?.flag ?? ""} {t(`country.${row.country}` as MessageKey)}
          </span>
        ),
      },
      {
        field: "region",
        headerName: t("table.region"),
        flex: 0.7,
        minWidth: 110,
        valueGetter: (v: string | null) => v ?? "—",
      },
      {
        field: "network",
        headerName: t("table.network"),
        flex: 0.7,
        minWidth: 110,
        renderCell: ({ row }) => (
          <Stack sx={{ lineHeight: 1.3, py: 0.5 }}>
            <Typography variant="body2" noWrap>
              {row.network ?? "—"}
            </Typography>
            {row.carrier && (
              <Typography variant="caption" color="text.secondary" noWrap>
                {row.carrier}
              </Typography>
            )}
          </Stack>
        ),
      },
      {
        field: "apiKeyPrefix",
        headerName: t("devices.apiKey"),
        flex: 0.8,
        minWidth: 120,
        renderCell: ({ row }) => (
          <span className="mono">{row.apiKeyPrefix}…</span>
        ),
      },
      {
        field: "lastSeenAt",
        headerName: t("devices.lastSeen"),
        flex: 0.9,
        minWidth: 140,
        renderCell: ({ row }) => (
          <Stack sx={{ lineHeight: 1.3, py: 0.5, minWidth: 0 }}>
            <Tooltip title={fmtDate(row.lastSeenAt)}>
              <Typography variant="body2" noWrap>
                {fmtAgo(row.lastSeenAt)}
              </Typography>
            </Tooltip>
            {row.lastIp && (
              <Tooltip title={row.lastUserAgent ?? ""}>
                <Typography variant="caption" color="text.secondary" noWrap className="mono">
                  {row.lastIp}
                </Typography>
              </Tooltip>
            )}
          </Stack>
        ),
      },
      {
        field: "actions",
        type: "actions",
        headerName: "",
        width: 56,
        getActions: ({ row }) => [
          <GridActionsCellItem
            key="results"
            icon={<ReceiptLongIcon fontSize="small" />}
            label={t("devices.results")}
            onClick={() => setViewing(row)}
            showInMenu
          />,
          <GridActionsCellItem
            key="history"
            icon={<HistoryIcon fontSize="small" />}
            label={t("devices.history")}
            onClick={() => navigate(`checks?deviceId=${row.id}`)}
            showInMenu
          />,
          <GridActionsCellItem
            key="edit"
            icon={<EditOutlinedIcon fontSize="small" />}
            label={t("common.edit")}
            onClick={() => setEditing(row)}
            showInMenu
          />,
          <GridActionsCellItem
            key="toggle"
            icon={
              row.disabled ? <LockOpenIcon fontSize="small" /> : <LockIcon fontSize="small" />
            }
            label={row.disabled ? t("devices.unlock") : t("devices.lock")}
            disabled={busy === `toggle-${row.id}`}
            onClick={() =>
              run(`toggle-${row.id}`, () => api.devices.update(row.id, { disabled: !row.disabled }))
            }
            showInMenu
          />,
          <GridActionsCellItem
            key="rotate"
            icon={<AutorenewIcon fontSize="small" />}
            label={t("devices.rotate")}
            disabled={busy === `rotate-${row.id}`}
            onClick={() => rotate(row)}
            showInMenu
          />,
          <GridActionsCellItem
            key="delete"
            icon={<DeleteIcon fontSize="small" />}
            label={t("common.delete")}
            onClick={() => remove(row)}
            showInMenu
          />,
        ],
      },
    ],
    [t, fmtDate, fmtAgo, busy],
  );

  return (
    <div className="page">
      <header className="page-head">
        <div>
          <h1>{t("devices.title")}</h1>
          <p className="muted">
            {t("devices.subtitle", {
              active: devices.filter(d => !d.disabled).length,
              total: devices.length,
            })}
          </p>
        </div>
        <Button variant="primary" onClick={() => setEditing("new")}>
          {t("devices.add")}
        </Button>
      </header>

      {error && <ErrorBox message={error} />}
      {actionError && <ErrorBox message={actionError} />}

      <Card>
        {loading ? (
          <Loading />
        ) : (
          <DataTable
            rows={newestFirst(devices, "createdAt")}
            columns={columns}
            showToolbar
            loading={busy !== null}
            getRowClassName={({ row }) => (row.disabled ? "row-disabled" : "")}
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          />
        )}
      </Card>

      {editing && (
        <DeviceForm
          device={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={created => {
            setEditing(null);
            if (created?.apiKey) setRevealed(created);
            reload();
          }}
        />
      )}

      {revealed?.apiKey && <ApiKeyModal device={revealed} onClose={() => setRevealed(null)} />}

      {viewing && <DeviceResultsModal device={viewing} onClose={() => setViewing(null)} />}
    </div>
  );
}
